import isEqual from 'lodash/isEqual';
import { makeObservable, override } from 'mobx';

import { PregnancyParams } from 'chaika-frontend-types/frontend-cms';
import { Locale, DictionaryNames } from 'chaika-enums';

import { PregnancyList } from '@store/models/SystemFields/Pregnancy';
import { AbstractListField, ListValueField } from './AbstractListField';
import { translate } from '@chaika/i18n/helper';
import { OptionType } from '@components/Select';
import { store } from '@store';

export class PregnancyField extends AbstractListField<PregnancyList, PregnancyParams> {
    public static getPregnancyOptions(): OptionType[] {
        const pregnancyDictionary = store.dictionary.getDictionary(DictionaryNames.Pregnancy);
        const locale = store.authUser.selectedLanguage ?? Locale.Ru;

        // Легаси значение
        const legacyPregnancyItemId = '3a0147df-08c7-4f4b-aee2-9da9919b14b3';
        const tChildbirthIsNaturalSecond = translate('childbirthIsNaturalSecond');

        return [
            {
                label: pregnancyDictionary.getTranslationTitle('5f09fcb7-6501-48fc-9330-01195d20614e', locale),
                value: '5f09fcb7-6501-48fc-9330-01195d20614e',
            },
            {
                label: pregnancyDictionary.getTranslationTitle('519e48c6-9753-44fb-9256-488da5dca12a', locale),
                value: '519e48c6-9753-44fb-9256-488da5dca12a',
            },
            {
                label: pregnancyDictionary.getTranslationTitle('21e97dbd-a9bd-4fbc-91d4-20b9b5e47dd3', locale),
                value: '21e97dbd-a9bd-4fbc-91d4-20b9b5e47dd3',
            },
            {
                label: pregnancyDictionary.getTranslationTitle('96355e57-3ab2-4509-8fe5-0f8b48878049', locale),
                value: '96355e57-3ab2-4509-8fe5-0f8b48878049',
            },
            {
                label: tChildbirthIsNaturalSecond,
                value: legacyPregnancyItemId,
            },
            {
                label: pregnancyDictionary.getTranslationTitle('f1c1497c-97ba-4bfa-8e80-8fefe5ef717f', locale),
                value: 'f1c1497c-97ba-4bfa-8e80-8fefe5ef717f',
            },
            {
                label: pregnancyDictionary.getTranslationTitle('a6de8a1d-d0ac-4be6-b121-7d3d880488f2', locale),
                value: 'a6de8a1d-d0ac-4be6-b121-7d3d880488f2',
            },
        ];
    }

    public static isCreateParamsValid({ id, year, gestationalAge = '', result = '' }: PregnancyParams): boolean {
        return !!id && (!!parseInt(`${year}`) || gestationalAge.length > 0 || result.length > 0);
    }

    constructor(params: PregnancyParams[], name: string) {
        super(params, name);
        makeObservable(this);
    }

    @override
    public get hasData(): boolean {
        return this.fieldValue.serialize().length > 0;
    }

    public isEqual(value: PregnancyList): boolean {
        const pregnancy = this.makeUpdateParams();

        return isEqual(pregnancy, value.serialize());
    }

    public makeUpdateParams(): PregnancyParams[] {
        return this.fieldValue.serialize();
    }

    public getPreview(): string {
        const serialized = this.fieldValue.serialize();

        return serialized.map(pregnancy => this.getPregnancyRevision(pregnancy)).join('\n');
    }

    public getItemRevision(id: string): string {
        const pregnancy = this.fieldValue.getExisting(id);

        return this.getPregnancyRevision(pregnancy);
    }

    public getItemTitle(id: string): string {
        const pregnancy = this.fieldValue.getExisting(id);

        return `${pregnancy.year} ${pregnancy.gestationalAge}`;
    }

    public getPregnancyRevision(pregnancy: PregnancyParams): string {
        const pregnancyOptions = PregnancyField.getPregnancyOptions().find(option => option.value === pregnancy.result);

        const result = pregnancyOptions ? pregnancyOptions.label : '';

        const tYear = translate('year');
        const tTotal = translate('result');
        const tTerm = translate('term');

        const parts = [
            pregnancy.year && `${tYear} — ${pregnancy.year}.`,
            result && `${tTotal} — ${result}.`,
            pregnancy.gestationalAge && `${tTerm} — ${pregnancy.gestationalAge}.`,
        ];

        return parts.filter(item => !!item).join(' ');
    }

    protected setDefaultValueValue(): void {
        this.defaultNullValue = [];
    }

    protected setFieldValue(value: ListValueField<PregnancyParams>): void {
        const formattedValues = this.makeValueArray(value);
        this.fieldValue = new PregnancyList(formattedValues);
    }
}
